<template>
  <div
    class="media-item-wrapper rounded-lg w-100 h-100"
    :class="{
      'hover-effect': hover && !isDeleting,
      '--selected-item': isSelected && !isDeleting,
      'media-item-wrapper--deleting': isDeleting
    }"
    @mouseenter="hover = true"
    @mouseleave="hover = false"
    @click.stop="$emit('select')"
  >
    <div class="media-item rounded-lg overflow-hidden w-100 h-100">
      <!-- Image Preview -->
      <img
        v-if="getAdFileTypeId() === 1 || !getAdFileTypeId()"
        class="rounded-lg overflow-hidden w-100 h-100"
        :src="item.media"
        :alt="item.name"
        @error="handleImageError"
      />
      
      <!-- Video Preview -->
      <div
        v-else-if="getAdFileTypeId() === 6"
        class="media-preview-container video-preview rounded-lg overflow-hidden w-100 h-100"
        :style="{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }"
      >
        <div class="d-flex align-items-center justify-content-center h-100">
          <i class="fa-solid fa-video" style="font-size: 48px; color: white;"></i>
        </div>
      </div>
      
      <!-- HTML5 ZIP Preview -->
      <div
        v-else-if="getAdFileTypeId() === 9"
        class="media-preview-container zip-preview rounded-lg overflow-hidden w-100 h-100"
        :style="{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }"
      >
        <div class="d-flex align-items-center justify-content-center h-100">
          <i class="fa-solid fa-file-zipper" style="font-size: 48px; color: white;"></i>
        </div>
      </div>
      
      <!-- Fallback -->
      <div
        v-else
        class="media-preview-container fallback-preview rounded-lg overflow-hidden w-100 h-100"
        style="background: #f0f0f0;"
      >
        <div class="d-flex align-items-center justify-content-center h-100">
          <i class="fa-solid fa-file" style="font-size: 48px; color: #909399;"></i>
        </div>
      </div>
    </div>
    <div class="media-file-name h-100 w-100 inset-0 absolute"></div>
    <div class="media-file-name-wrapper absolute w-full inset-0">
      <span class="--file-name">{{ item.name }}</span>
      <span class="--file-dimention">{{ item.dimensions || (getAdFileTypeId() === 6 ? 'Dimensions not detected' : '') }}</span>
      <!-- Ad Type Badge -->
      <el-tag 
        :type="getAdTypeTagType()"
        size="mini"
        effect="dark"
        class="ad-type-badge">
        {{ getAdTypeName() }}
      </el-tag>
    </div>
    <!-- Quick actions on hover -->
    <button
      class="preview-btn"
      title="Preview"
      @click.stop="$emit('preview')"
      @mousedown.stop.prevent
      @mouseup.stop
      @touchstart.stop.prevent
    >
      <i class="el-icon-view"></i>
    </button>
    <!-- Selected indicator checkmark -->
    <div
      v-if="isSelected && !isDeleting"
      class="selected-checkmark"
    >
      <i class="el-icon-check"></i>
    </div>
    <div
      v-if="isDeleting"
      class="deleting-overlay d-flex align-items-center justify-content-center"
    >
      <i class="el-icon-loading" style="font-size:32px;color:white;"></i>
    </div>
    <!-- Selected overlay (always visible when selected, not just on hover) -->
    <div
      v-if="isSelected && !isDeleting"
      class="selected-overlay h-100 w-100 rounded-lg --media-overlay"
    ></div>
    <div
      v-else-if="hover"
      class="media-gradient h-100 w-100 rounded-lg --media-overlay"
    ></div>
  </div>
</template>

<script>
import { Modal } from "src/components";
import { Select, Option, Upload, Button, Checkbox, Tag } from "element-ui";
export default {
  components: {
    Modal,
    [Option.name]: Option,
    [Select.name]: Select,
    [Upload.name]: Upload,
    [Button.name]: Button,
    [Checkbox.name]: Checkbox,
    [Tag.name]: Tag
  },
  data() {
    return {
      hover: false
    };
  },
  computed: {
    isSelected() {
      return this.selectedItems.some(i => i.id === this.item.id);
    },
  },
  methods: {
    /**
     * Get ad file type ID from media item
     */
    getAdFileTypeId() {
      if (!this.item) return 1;
      
      // Check if ad_file_type_id is explicitly set
      if (this.item.ad_file_type_id) {
        return this.item.ad_file_type_id;
      }
      
      // Detect from filename (more reliable than URL)
      const fileName = (this.item.name || this.item.media_name || '').toLowerCase();
      const url = (this.item.media || this.item.url || '').toLowerCase();
      
      // Combine filename and URL for detection
      const combined = fileName + ' ' + url;
      
      // Check for video extensions (check filename first, then URL)
      if (combined.match(/\.(mp4|avi|mov|mpeg|webm)(\?|$| )/i)) {
        return 6; // Video
      }
      
      // Check for HTML5 ZIP (check filename first, then URL)
      if (combined.match(/\.zip(\?|$| )/i)) {
        return 9; // HTML5
      }
      
      // Check for image extensions (check filename first, then URL)
      if (combined.match(/\.(jpg|jpeg|png|gif)(\?|$| )/i)) {
        return 1; // Image
      }
      
      // Default to image if unknown (backward compatibility)
      return 1;
    },
    
    /**
     * Get ad type name for display
     */
    getAdTypeName() {
      const adFileTypeId = this.getAdFileTypeId();
      const types = {
        1: 'Image',
        6: 'Video',
        9: 'HTML5'
      };
      return types[adFileTypeId] || 'Unknown';
    },
    
    /**
     * Get Element UI tag type for ad type badge
     */
    getAdTypeTagType() {
      const adFileTypeId = this.getAdFileTypeId();
      const types = {
        1: 'info',      // Blue for Image
        6: 'warning',   // Orange for Video
        9: 'success'    // Green for HTML5
      };
      return types[adFileTypeId] || '';
    },
    
    /**
     * Handle image load error - show fallback icon
     */
    handleImageError(event) {
      const container = event.target.parentElement;
      container.innerHTML = `
        <div class="media-preview-container fallback-preview rounded-lg overflow-hidden w-100 h-100" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center;">
          <i class="fa-solid fa-image" style="font-size: 48px; color: #909399;"></i>
        </div>
      `;
    }
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    isDeleting: {
      type: Boolean,
      default: false 
    }
  },
};
</script>

<style lang="scss">
.deleting-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 10;
}
.media-item-wrapper--deleting {
  pointer-events: none;
  cursor: not-allowed;
}
.media-item {
  cursor: pointer;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
  aspect-ratio: 1 / 1;
  height: 100% !important;
  background-color: #fff !important;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  position: relative;
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05);
    z-index: 1;
  }
  img {
    object-fit: cover;
    overflow: hidden;
  }
}
.media-item-wrapper {
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
}
.preview-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: rgba(255,255,255,0.95);
  color: #111827; /* gray-900 */
  box-shadow: 0 1px 2px rgba(0,0,0,0.12);
  z-index: 30;
  pointer-events: auto;
}
.preview-btn:hover { background: #ffffff; }
.rounded-lg {
  border-radius: 0.5rem;
}
.media-file-name {
  cursor: pointer;
  background: linear-gradient(
    0deg,
    rgb(34, 34, 34) 0%,
    rgba(255, 255, 255, 0) 75%
  );
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  position: absolute;
}
.media-file-name-wrapper {
  width: 100%;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  position: absolute;
  z-index: 1;
  display: flex;
  flex-direction: column;
  span {
    max-width: 80%;
    color: rgba(255, 255, 255, 1);
    font-weight: 700;
    font-size: 0.75rem;
    line-height: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
   
    left: 0.5rem;
    position: absolute;
    
  }
  .--file-name{
    bottom: 2.5rem;
  }
  .--file-dimention{
     bottom: 1rem;
  }
  
  .ad-type-badge {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 2;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
  }
}

.media-preview-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.media-gradient {
  background: rgba(6, 5, 5, 0.6);
  cursor: pointer;
}
.selected-file-gradient {
  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.64) 100%
    ),
    linear-gradient(0deg, rgba(21, 94, 239, 0.15), rgba(21, 94, 239, 0.15));
  cursor: pointer;
}
@keyframes fadeInOpacity {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.image-hover-media {
  opacity: 1;
  animation-name: fadeInOpacity;
  animation-iteration-count: 1;
  animation-timing-function: ease-in;
  animation-duration: 0.3s;
  cursor: pointer;
}
.--media-overlay {
  inset: 0;
  position: absolute;
}
.--selected-item {
  border: 3px solid #409EFF !important;
  border-radius: 0.5rem;
  padding: 0.125rem;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2), 0 4px 12px rgba(64, 158, 255, 0.3) !important;
  background-color: rgba(64, 158, 255, 0.05);
}

.selected-checkmark {
  position: absolute;
  bottom: 0.5rem;
  left: 0.5rem;
  width: 28px;
  height: 28px;
  background: #67C23A;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;
  box-shadow: 0 2px 8px rgba(103, 194, 58, 0.4);
  animation: checkmarkPop 0.2s ease-out;
}

.selected-checkmark i {
  color: white;
  font-size: 16px;
  font-weight: bold;
}

@keyframes checkmarkPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.selected-overlay {
  background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.64) 100%
    ),
    linear-gradient(0deg, rgba(64, 158, 255, 0.25), rgba(64, 158, 255, 0.25));
  cursor: pointer;
  z-index: 1;
}
</style>
